"use client";

import {
	deriveAddToCartState,
	isCartDisabledByState
} from "@/composites/cart/AddToCartButton";
import {
	type CatalogLabel,
	type CatalogProduct,
	type CatalogScheduledLabel,
	type OnlineStatus
} from "@/domain/catalog";
import {
	isConsultantOnly,
	isPriceVisible
} from "@/domain/catalog/online-status";
import { useEffect, type ReactNode } from "react";
import {
	ProductSummaryView,
	type ProductSummaryViewProps
} from "./ProductSummaryView";

export interface ProductSummaryProps {
	product: CatalogProduct;
	isConsultant?: boolean;
	onError?: (message: string) => void;
	onSuccess?: () => void;
	addToList?: ReactNode;
	className?: string;
	quantity: number;
	onQuantityChange: (quantity: number) => void;
	addToCart: {
		onClick: () => void | Promise<void>;
		loading: boolean;
		error: string | null;
	};
	paymentMessage?: ReactNode;
}

/**
 * Wired ProductSummary — derives every input to `ProductSummaryView` from
 * a `CatalogProduct`, quantity state, and the AddToCart handler props.
 * Filters `labels[]` + `scheduledLabels[]` (date-windowed) into the two
 * kind buckets the View renders separately.
 */
export function ProductSummary({
	product,
	isConsultant = false,
	onError,
	addToList,
	className,
	quantity,
	onQuantityChange,
	addToCart,
	paymentMessage
}: ProductSummaryProps) {
	useEffect(() => {
		if (addToCart.error && onError) onError(addToCart.error);
	}, [addToCart.error, onError]);

	const showPrice = isPriceVisible(product.onlineStatus);
	const display = product.salePrice;
	const list = product.listPrice;
	const isOnSale =
		typeof display === "number" &&
		typeof list === "number" &&
		display < list;

	const ratingProp: ProductSummaryViewProps["rating"] =
		typeof product.starsRating === "number" &&
		typeof product.numRatings === "number" &&
		product.numRatings > 0
			? {
					value: product.starsRating,
					count: product.numRatings,
					reviewsHref: "#reviews"
				}
			: undefined;

	const availabilityTag = computeAvailabilityTag(product.onlineStatus);

	const addToCartState = deriveAddToCartState(product, {
		isConsultant,
		loading: addToCart.loading,
		error: addToCart.error
	});

	const cartDisabled = isCartDisabledByState(addToCartState.kind);
	const canAddToLists =
		product.onlineStatus !== "discontinued" &&
		product.onlineStatus !== "inactive";

	const { offerLabels, urgentLabels } = bucketLabels(
		product.labels,
		product.scheduledLabels
	);

	return (
		<ProductSummaryView
			title={product.title}
			sku={product.sku}
			rating={ratingProp}
			pricing={
				showPrice
					? {
							display,
							strikeThrough: isOnSale ? list : undefined,
							halfPriceCombo: product.halfPriceComboEligible,
							consultantOnly:
								isConsultantOnly(product.availableTo) &&
								isConsultant
						}
					: undefined
			}
			urgentLabels={urgentLabels}
			offerLabels={offerLabels}
			availabilityTag={availabilityTag}
			quantity={
				cartDisabled
					? undefined
					: {
							value: quantity,
							onChange: onQuantityChange,
							min: 1,
							max: 99
						}
			}
			addToCart={{
				state: addToCartState,
				onClick: addToCart.onClick
			}}
			addToList={canAddToLists ? addToList : undefined}
			paymentMessage={showPrice ? paymentMessage : undefined}
			className={className}
		/>
	);
}

/**
 * Splits `labels` + date-active `scheduledLabels` into the two kind buckets
 * the ProductSummaryView renders separately. Schedule windows: a label is
 * active if `now` falls within `[startDate, endDate]` (either bound optional).
 */
function bucketLabels(
	labels: CatalogLabel[],
	scheduledLabels: CatalogScheduledLabel[]
): { offerLabels: CatalogLabel[]; urgentLabels: CatalogLabel[] } {
	const now = Date.now();
	const activeScheduled = scheduledLabels.filter((l) => {
		if (l.startDate && Date.parse(l.startDate) > now) return false;
		if (l.endDate && Date.parse(l.endDate) < now) return false;
		return true;
	});
	const all = [...labels, ...activeScheduled];

	return {
		offerLabels: all.filter((l) => l.kind === "product_offer"),
		urgentLabels: all.filter((l) => l.kind === "urgent_message")
	};
}

function computeAvailabilityTag(
	status: OnlineStatus
): { label: string; color: "error" | "grey" } | undefined {
	switch (status) {
		case "pre-launch":
			return { label: "Coming Soon", color: "grey" };
		case "out-of-stock":
			return { label: "Out of Stock", color: "error" };
		case "temporarily-unavailable":
			return { label: "Currently Unavailable", color: "grey" };
		case "discontinued":
		case "inactive":
			return { label: "Discontinued", color: "grey" };
		default:
			return undefined;
	}
}
